/** @jsx jsx */
import { memo, useCallback } from 'react';
import { css, jsx } from '@compiled/react';
import { useProject } from '@atlassian/jira-business-entity-project-hook/src/index.tsx';
import { CORE_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { IssueLinksStats } from '@atlassian/jira-issue-links-stats-business/src/index.tsx';
import type { BoardIssueId, BoardIssueIssueTypeField } from '../../../../../../common/types.tsx';
import { useRefetchIssues } from '../../../../../../controllers/refetch-issues/index.tsx';

type Props = {
	issueKey: string;
	issueId: BoardIssueId;
	issueSummary: string;
	issueLinksCount: number;
	issueTypeField?: BoardIssueIssueTypeField;
};

const IssueLinksNotMemo = ({
	issueKey,
	issueId,
	issueSummary,
	issueLinksCount,
	issueTypeField,
}: Props) => {
	const {
		permissions: { linkIssues },
	} = useProject();
	const refetchIssues = useRefetchIssues();

	const onIssueUnlink = useCallback(
		(issueIds: string[]) => {
			refetchIssues(issueIds.map(Number));
		},
		[refetchIssues],
	);

	const onIssueLink = useCallback(
		(originalIssueId: string, updateIssueIds: string[]) => {
			const issueIds = [originalIssueId, ...updateIssueIds];
			refetchIssues(issueIds.map(Number));
		},
		[refetchIssues],
	);

	return (
		<div css={pointerEventsStyles}>
			<IssueLinksStats
				testId="work-management-board.ui.board.column.card.card-footer.issue-links"
				issueLinksCount={issueLinksCount}
				issueKey={issueKey}
				issueId={String(issueId)}
				issueSummary={issueSummary}
				issueType={issueTypeField?.issueType}
				feedbackCollectorPrefix="business-board"
				isLinkingEnabled={linkIssues}
				sourceProductType={CORE_PROJECT}
				onIssueUnlink={onIssueUnlink}
				onIssueLink={onIssueLink}
				maxIssueLinksStatsCount={9}
			/>
		</div>
	);
};

export const IssueLinks = memo(IssueLinksNotMemo);

// These styles allow elements to receive pointer events even with the use of the
// InteractionLayer component in the board card component. The InteractionLayer
// component is used to create a clickable area for the card, but we want to
// ensure that certain elements within the card can still receive pointer events.
const pointerEventsStyles = css({
	position: 'relative',
	pointerEvents: 'auto',
});
